/* eslint-disable react/display-name */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Page } from 'components/Page';
import { Row, Column, Link, Loading, ContentSwitcher, Switch, ComboBox, Button } from '@carbon/react';
import { Add, TrashCan, Select_01 } from '@carbon/react/icons';
import { useGetStreamsQuery } from 'services/streams';

import {
  Panel,
  PanelHeader,
  PanelFooter,
  PanelLink,
  PanelLabel,
} from 'components/Panel';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-cool-inview';
import ListItem from '@/components/List/ListItem';
import { SearchBar } from '@/components/SearchBar';
import useUser from '@/lib/hooks/useUser';
import useModal from '@/lib/hooks/useModal';
import { useGetOrganisationsQuery } from '@/redux/new_services/organisations';

const QUERY_PER_REQUEST = 20;

const Index = ({ modal = false, isSearchable = true, doSelect, forStreamID=0, size= 'large', openStreamEditPage=() => {}}) => {

  const { isAdmin } = useUser();
  //const [sizedValue, setSize] = useState(size);
  const [items, setItems] = useState([]);
  const [lastArgs, setLastArgs] = useState("");
  const [lastRequestID, setLastRequestID] = useState("");
  // Admin-only stream-kind toggle. Non-admins never see the switcher and
  // the backend forces `normal` for them anyway, so the filter value is
  // a UI convenience for admins. Stream-modal usages (when picking a
  // stream-in-stream) stay on `normal` — device streams can't be reused.
  const [streamKind, setStreamKind] = useState('normal');
  const [organisationId, setOrganisationId] = useState(0);
  const [query, setQuery] = useState({skip: 0, filter: {text: '', forStreamID: Number(forStreamID) || 0, modal: modal, kind: 'normal'}, limit: QUERY_PER_REQUEST});

  const { data: organisations = [] } = useGetOrganisationsQuery(undefined, { skip: !isAdmin || modal });
  //const [totalSize, setTotalSize] = useState(0);

  const {
    data = { data: [], count: 0},
    originalArgs,
    requestId,
    isFetching,
  } = useGetStreamsQuery(query, {refetchOnMountOrArgChange: true});

  const [checked, setChecked] = useState([]);

  // Selection mode (non-modal only): operator flips the toolbar into a
  // multi-select state, the gallery's item-click is repurposed to toggle
  // membership in selectedStreams, and the toolbar grows a bulk-delete
  // button. Mirrors the same flow ElementsGallery uses for elements.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedStreams, setSelectedStreams] = useState([]);
  const [confirmRemoveStreams] = useModal('DELETE_STREAMS');

  useEffect(() => {
    // Leaving selection mode also drops the current selection — otherwise
    // re-entering selection would resurrect a stale pick from another
    // search / filter context.
    if (!selectionMode) setSelectedStreams([]);
  }, [selectionMode]);

  useEffect(() => {
    // wir wissen das das ein neuer request ist....
    // somit müssten eigentlich auch die daten neu sein...
    // wenn nicht, dann ist es ein fehler
    if (lastRequestID && lastRequestID === requestId && isFetching) {
      return;
    }

    setLastRequestID(requestId);
    if (originalArgs && originalArgs.skip === 0) {
      setItems([{
        items: data.data,
        args: originalArgs,
      }]);
    } else {
      if (originalArgs !== lastArgs && data.data?.length > 0) {
        setItems((i) => [...i, {
          items: data.data,
          args: originalArgs,
        }]);
      }
    }
    if (originalArgs !== lastArgs) {
      setLastArgs(originalArgs);
    }
  }, [data]); //eslint-disable-line react-hooks/exhaustive-deps

  //useEffect(() => {
  //  setTotalSize(items.reduce((sum, inner) => sum + inner?.items?.length, 0));
  //}, [items]);

  const { observe } = useInView({
    threshold: 0.5, // default is 0
    //rootMargin: "-50px 0px",
    // When the last item comes to the viewport
    onEnter: ({ unobserve }) => {
      // Pause observe when loading data
      // Load more data
      const count = data?.count || 0;
      const real_result = (data?.data || []).length;
      const theSize = (items || []).reduce((sum, inner) => sum + (inner.items || []).length, 0);
      console.info('is in view load more...count:', count, ' isFetching:', isFetching, ' combindedData:', theSize);
      if (count >= 0 && theSize < count && !isFetching && real_result > 0) {
        //console.info('can load more...old skip:', query.skip, " new skip:", query.skip + 10);
        //unobserve();
        setQuery(f => { return {...f, skip: f.skip + QUERY_PER_REQUEST}});
      } else {
        console.info('cannot load more...');
      }
    },
  }); // https://www.npmjs.com/package/react-cool-inview#infinite-scroll

  // select item callback
  const selectItem = useCallback(
    (item) => {
      setChecked(
        checked.find((i) => i.model.ID === item.model.ID)
          ? checked.filter((i) => i.model.ID !== item.model.ID)
          : [...checked, item]
      );
    },
    [setChecked, checked]
  );

  useEffect(() => {
    if (checked && doSelect) {
      doSelect(checked);
    }
  }, [checked, doSelect]);

  // Toggle membership for the bulk-action selection list.
  const toggleStreamSelected = useCallback((item) => {
    setSelectedStreams((sel) =>
      sel.find((i) => i.model.ID === item.model.ID)
        ? sel.filter((i) => i.model.ID !== item.model.ID)
        : [...sel, item]
    );
  }, []);

  const onClickItem = (item) => {
    if (modal) {
      selectItem(item);
      return;
    }
    if (selectionMode) {
      toggleStreamSelected(item);
      return;
    }
    openStreamEditPage(`/stream/${item.model.ID}`)
  };

  const clickDeleteSelectedStreams = useCallback(() => {
    if (selectedStreams.length === 0) return;
    confirmRemoveStreams({
      streams: selectedStreams,
      cb: () => {
        setSelectionMode(false);
        setSelectedStreams([]);
      },
    });
  }, [confirmRemoveStreams, selectedStreams]);

  const searchChanged = (searchtext) => {
    setQuery(f => { return {...f, skip: 0, filter: {...f.filter, text: searchtext}} });
  };

  // Switching back to "normal streams" drops the org filter — that view is
  // strictly scoped to the admin's own org (matches the pre-existing
  // behaviour), so an organisation_id left over from device-streams view
  // would just create confusion if the toggle is flipped back.
  useEffect(() => {
    if (streamKind !== 'device' && organisationId !== 0) {
      setOrganisationId(0);
    }
  }, [streamKind, organisationId]);

  // Push admin filter changes into the query so the visible list reflects
  // the new scope on the next refetch. Don't proactively reset `items`
  // here — the `[data]`-effect above re-populates it on every page-0
  // response, and clearing it on mount (when this effect fires with the
  // initial values) raced against that effect and wiped the list right
  // after it had been filled from the RTK cache.
  useEffect(() => {
    setQuery(f => ({
      ...f,
      skip: 0,
      filter: {
        ...f.filter,
        kind: streamKind,
        organisation_id: streamKind === 'device' && organisationId ? organisationId : undefined,
      },
    }));
  }, [streamKind, organisationId]);

  // Device-stream rows replace the "notes" line with a concise context tag
  // so the admin sees, per tile, which device + location + organisation
  // the stream belongs to. Pure display transform; the underlying records
  // stay untouched.
  const displayItems = useMemo(() => {
    return (items || []).map((batch) => ({
      ...batch,
      items: (batch.items || []).map((it) => {
        if (it?.kind !== 'device') return it;
        const parts = [];
        if (it.device_label) parts.push(`Gerät: ${it.device_label}`);
        if (it.location_name) parts.push(it.location_name);
        if (it.organisation_name) parts.push(it.organisation_name);
        const ctx = parts.join(' • ');
        return ctx ? { ...it, description: ctx, notes: ctx } : it;
      }),
    }));
  }, [items]);

  let fixedHeight = {};
  let overflowScroll = {};

  if (modal) {
    fixedHeight = {
      height: '100%',
    };
    overflowScroll = {
      overflow: 'auto',
    };
  }

  // Admin-only toolbar: switch between normal streams and admin-only device
  // streams. Hidden in modal mode (the modal is used for picking a
  // stream-in-stream and device streams aren't reusable, so the filter has
  // no value there). The org-scope dropdown only renders for the
  // device-streams listing — for normal streams admins stay locked to
  // their own organisation, matching the pre-existing behaviour.
  const adminToolbar = !modal && isAdmin && (
    <Row style={{ marginBottom: '0.75rem', alignItems: 'center', gap: '1rem' }}>
      <Column lg={6} md={4} sm={4}>
        <ContentSwitcher
          selectedIndex={streamKind === 'device' ? 1 : 0}
          onChange={({ name }) => setStreamKind(name)}
        >
          <Switch name="normal" text="Normale Streams" />
          <Switch name="device" text="Geräte-Streams" />
        </ContentSwitcher>
      </Column>
      {streamKind === 'device' && (
        <Column lg={6} md={4} sm={4}>
          <ComboBox
            id="streams-filter-organisation"
            titleText=""
            placeholder="Alle Organisationen"
            items={[
              { id: 0, name: 'Alle Organisationen' },
              ...((organisations || []).map(o => ({ id: o.id, name: o.name }))),
            ]}
            itemToString={(o) => (o ? o.name : '')}
            selectedItem={
              [{ id: 0, name: 'Alle Organisationen' },
                ...((organisations || []).map(o => ({ id: o.id, name: o.name })))]
                .find(o => o.id === organisationId) || { id: 0, name: 'Alle Organisationen' }
            }
            onChange={(e) => setOrganisationId(e.selectedItem ? e.selectedItem.id : 0)}
          />
        </Column>
      )}
    </Row>
  );

  return (
    <>
      {(adminToolbar || isSearchable) && (
        <div className={modal ? '' : 'page-sticky-toolbar'}>
          {adminToolbar}
          {isSearchable && (
            <SearchBar searchChanged={searchChanged} labelText={"Streams suchen..."}>
              {!modal && (
                <>
                  {selectionMode && (
                    <Button
                      hasIconOnly
                      renderIcon={TrashCan}
                      kind="danger"
                      onClick={clickDeleteSelectedStreams}
                      disabled={selectedStreams.length === 0}
                      tooltipPosition="bottom"
                      iconDescription={`Streams löschen (${selectedStreams.length})`}
                    />
                  )}
                  <Button
                    renderIcon={(props) => <Select_01 size={32} {...props}/>}
                    onClick={() => setSelectionMode((s) => !s)}>
                    {selectionMode ? 'Auswahl abbrechen' : 'Streams auswählen'}
                  </Button>
                </>
              )}
            </SearchBar>
          )}
        </div>
      )}
      <Row className="elements-page__r2" >
        <Column style={{ ...fixedHeight, ...overflowScroll }}>
          <Row className="list_container">
            {(displayItems || []).map((inner, midx) => {
              return inner?.items?.map((item, idx) => {
                //const theSize = items.reduce((sum, inner) => sum + inner.items.length, 0);
                return (
                  <ListItem
                    item={item}
                    key={(midx + 1) * (idx + 1) + "-" + item.model.ID}
                    name={item.name}
                    onClick={onClickItem}
                    bottomFooter
                    isSelected={
                      (selectionMode && selectedStreams.find((i) => i.model.ID === item.model.ID) !== undefined) ||
                      (modal && checked.find((i) => i.model.ID === item.model.ID) !== undefined)
                    }
                    size={size}
                  />
                );
              })
            })}
            { isFetching ? <Loading active={true} withOverlay={true} /> : <div ref={observe}></div> }
          </Row>
        </Column>
      </Row>
    </>
  );
};

export const StreamsModalIndex = ({onSelectionChange, forStreamID=0}) => {
  return <Index modal doSelect={onSelectionChange} forStreamID={forStreamID} />;
}

const StreamsIndex = ({ onSelectionChange, forStreamID=0}) => {
  const history = useNavigate();
  const gallery = <Index doSelect={onSelectionChange} forStreamID={forStreamID} openStreamEditPage={(url) => {
    history(url);
  }} />;


  return (
    <Page
      pageClass="stream-page"
      mainContentClass="streams-page__r2"
      pageTitle="Streams">
      <Row>
        <Column md={8} lg={8} sm={4}>
          <Panel halfSize>
            <PanelHeader header>Beschreibung</PanelHeader>
            <PanelLabel>
              Ein Stream ist deine Abspielliste: er bestimmt, welche Bilder
              und Videos in welcher Reihenfolge auf deinen Bildschirmen
              laufen. Lege neue Streams für unterschiedliche Anlässe oder
              Standorte an und passe sie jederzeit an, ohne etwas am Gerät
              ändern zu müssen.
            </PanelLabel>
            <Link href="#" onClick={() => history('/help')}>
              Hilfe zu Streams
            </Link>
            <PanelFooter></PanelFooter>
          </Panel>
        </Column>

        <Column md={4} lg={4} sm={4}>
          <Panel halfSize>
            <PanelHeader header>
              Stream erstellen
            </PanelHeader>
            <PanelLabel>
              Fange gleich an und erstelle einen Stream
            </PanelLabel>
            <PanelFooter>
              <PanelLink
                centered
                href="#"
                onClick={() => history('/stream/new')}
                icon={<Add size={32} />}>
                STREAM ERSTELLEN
              </PanelLink>
            </PanelFooter>
          </Panel>
        </Column>

        {/**<Column md={2}>
          <Panel halfSize>
            <PanelHeader small centered>
              Standart Stream
            </PanelHeader>
            <PanelLabel centered>
              Wähle einen Standard Stream aus, der automatisch bei jedem neu
              eingerichtetem micast system angewendet wird.
            </PanelLabel>
            <PanelFooter>
              <PanelLink
                centered
                href="#"
                onClick={() => history('/stream/new')}>
                STREAM WÄHLEN
              </PanelLink>
            </PanelFooter>
          </Panel>
        </Column>**/}
      </Row>
      <Row>
        <Column>{gallery}</Column>
      </Row>
    </Page>
  );
};

export default StreamsIndex;
